import { isValidEmail, normalizeEmail } from "@/lib/validation/email";

/**
 * Where the public estimator's email gate stands. `preview`: the sample shows, untouched.
 * `locked`: the visitor reached for it, so the results blur behind an email ask. A saved email
 * navigates away, so there is no success state.
 */
export type Gate =
  | { status: "preview" }
  | { status: "locked" }
  | { status: "submitting"; email: string }
  | { status: "error"; email: string; message: string };

/** What moves the gate. */
export type GateEvent =
  | { type: "interact" }
  | { type: "submit"; email: string }
  | { type: "fail"; email: string; message: string };

/** Shown for an address that isn't one. */
export const INVALID_EMAIL_MESSAGE = "Enter a valid email, like you@company.com";

/** Shown when the email couldn't be saved. */
export const SAVE_FAILED_MESSAGE = "Something went wrong. Try again in a moment.";

/**
 * Reads a typed email.
 * @param raw - What the visitor typed.
 * @returns The normalized email, or why it isn't one.
 * @throws Never.
 */
export const parseWorkEmail = (raw: string): { ok: true; email: string } | { ok: false; message: string } => {
  const email = normalizeEmail(raw.trim());
  return isValidEmail(email) ? { ok: true, email } : { ok: false, message: INVALID_EMAIL_MESSAGE };
};

/**
 * The gate's next state. The first interaction locks it and later ones change nothing; a submit
 * while one is out is ignored.
 * @param gate - The current state.
 * @param event - What happened.
 * @returns The next state.
 * @throws Never.
 */
export const gateReducer = (gate: Gate, event: GateEvent): Gate => {
  switch (event.type) {
    case "interact":
      return gate.status === "preview" ? { status: "locked" } : gate;
    case "submit":
      return gate.status === "submitting" ? gate : { status: "submitting", email: event.email };
    case "fail":
      return { status: "error", email: event.email, message: event.message };
  }
};
